import { formatNumberToDate } from '../../../../utils/formatting';
import type { AuditGrid } from './audit-grid';

function formatDateTime(datetime: number) {
  const date = new Date(datetime);
  const stringData = date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return stringData;
}

export enum COLUMN_RFQ_FIELDS {
  AUDIT_EVENT_TYPE = 'AUDIT_EVENT_TYPE',
  AUDIT_EVENT_DATETIME = 'AUDIT_EVENT_DATETIME',
  AUDIT_EVENT_USER = 'AUDIT_EVENT_USER',
  RECORD_CREATED = 'CREATED_AT',
  RECORD_MODIFIED = 'MODIFIED_AT',
  UNIQUE_ID = 'UNIQUE_ID',
  RFQ_ID = 'RFQ_ID',
  CREATED_BY = 'CREATED_BY',
  MODIFIED_BY = 'MODIFIED_BY',
  CASCADE_COMPANY_ID = 'CASCADE_COMPANY_ID',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  NODAL_LME_HISTORIC_MIN = 'NODAL_LME_HISTORIC_MIN',
  ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS = 'ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS',
  RFQ_SEQUENCE = 'RFQ_SEQUENCE',
  GUARANTEED_COD = 'GUARANTEED_COD',
  RFQ_APPLICABLE_REC_PROGRAM = 'RFQ_APPLICABLE_REC_PROGRAM',
  PROPOSAL_SENDER = 'PROPOSAL_SENDER',
  SETTLEMENT_INDEX_STRING = 'SETTLEMENT_INDEX_STRING',
  UPSIDE_SHARING_FIXED_PRICE = 'UPSIDE_SHARING_FIXED_PRICE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  LME_RANGE_LOWER_BOUND = 'LME_RANGE_LOWER_BOUND',
  RFQ_REFERENCE = 'RFQ_REFERENCE',
  RFQ_STATUS = 'RFQ_STATUS',
  STATE_NAME = 'STATE_NAME',
  COUNTRY_NAME = 'COUNTRY_NAME',
  NEGOTIATION_STATUS = 'NEGOTIATION_STATUS',
  FIXED_PRICE = 'FIXED_PRICE',
  CAPACITY = 'CAPACITY',
  TENOR_START_DATE = 'TENOR_START_DATE',
  FIXED_PRICE_LOWER_BOUND = 'FIXED_PRICE_LOWER_BOUND',
  SELLER_ALLOCATED_UPSIDE_PERCENTAGE = 'SELLER_ALLOCATED_UPSIDE_PERCENTAGE',
  NODAL_LME_HISTORIC_MAX = 'NODAL_LME_HISTORIC_MAX',
  FIXED_PRICE_UPPER_BOUND = 'FIXED_PRICE_UPPER_BOUND',
  BUYER_UPSIDE_ALLOCATION = 'BUYER_UPSIDE_ALLOCATION',
  EXPECTED_SETTLEMENT_MIN = 'EXPECTED_SETTLEMENT_MIN',
  ESTIMATED_DOWNSIDE_RISK_UPPER_BOUND = 'ESTIMATED_DOWNSIDE_RISK_UPPER_BOUND',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  REQUESTED_VOLUME = 'REQUESTED_VOLUME',
  RFQ_LEGAL_ENTITY = 'RFQ_LEGAL_ENTITY',
  CITY_NAME = 'CITY_NAME',
  YEAR_ENTERED = 'YEAR_ENTERED',
  EXPECTED_SETTLEMENT_MAX = 'EXPECTED_SETTLEMENT_MAX',
  RFQ_PERFORMANCE_GUARANTEE_METHOD = 'RFQ_PERFORMANCE_GUARANTEE_METHOD',
  MONTH_ENTERED = 'MONTH_ENTERED',
  UPSIDE_SHARING = 'UPSIDE_SHARING',
  DOWNSIDE_RISK_MAX = 'DOWNSIDE_RISK_MAX',
  COLLAR = 'COLLAR',
  BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE = 'BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE',
  RFQ_NEGATIVE_PRICING_METHOD = 'RFQ_NEGATIVE_PRICING_METHOD',
  BUYER_UPSIDE_ALLOCATION_PRICE = 'BUYER_UPSIDE_ALLOCATION_PRICE',
  TARGET_COD = 'TARGET_COD',
  COLLAR_CAP_PRICE = 'COLLAR_CAP_PRICE',
  DOWNSIDE_RISK_MIN = 'DOWNSIDE_RISK_MIN',
  TECHNOLOGY = 'TECHNOLOGY',
  CARBON_FREE_ENERGY_LOWER_BOUND = 'CARBON_FREE_ENERGY_LOWER_BOUND',
  EXPECTED_SETTLEMENT_VALUE_LOWER_BOUND = 'EXPECTED_SETTLEMENT_VALUE_LOWER_BOUND',
  CARBON_FREE_ENERGY_UPPER_BOUND = 'CARBON_FREE_ENERGY_UPPER_BOUND',
  ISO = 'ISO',
  RFQ_AUDIT_ID = 'RFQ_AUDIT_ID',
  STATE_ABBREVIATION = 'STATE_ABBREVIATION',
  IMPLIED_CARBON_PRICE_MAX = 'IMPLIED_CARBON_PRICE_MAX',
  COLLAR_FLOOR_PRICE = 'COLLAR_FLOOR_PRICE',
  LME_RANGE_UPPER_BOUND = 'LME_RANGE_UPPER_BOUND',
  TICKER = 'TICKER',
  ESTIMATED_DOWNSIDE_RISK_LOWER_BOUND = 'ESTIMATED_DOWNSIDE_RISK_LOWER_BOUND',
  EXPECTED_SETTLEMENT_VALUE_UPPER_BOUND = 'EXPECTED_SETTLEMENT_VALUE_UPPER_BOUND',
  NEGOTIATION_ID = 'NEGOTIATION_ID',
  RFQ_REC_DELIVERY_METHODOLOGY = 'RFQ_REC_DELIVERY_METHODOLOGY',
  IMPLIED_CARBON_PRICE_MIN = 'IMPLIED_CARBON_PRICE_MIN',
  HUB_ZONE_MATRIX_ID = 'HUB_ZONE_MATRIX_ID',
  BUYER_POST_COD_COLLATERAL = 'BUYER_POST_COD_COLLATERAL',
  AUDIT_EVENT_TEXT = 'AUDIT_EVENT_TEXT',
}

export enum COLUMN_VPPA_FIELDS {
  AUDIT_EVENT_TYPE = 'AUDIT_EVENT_TYPE',
  AUDIT_EVENT_DATETIME = 'AUDIT_EVENT_DATETIME',
  AUDIT_EVENT_USER = 'AUDIT_EVENT_USER',
  RECORD_CREATED = 'CREATED_AT',
  RECORD_MODIFIED = 'MODIFIED_AT',
  UNIQUE_ID = 'UNIQUE_ID',
  VPPA_ID = 'VPPA_ID',
  CREATED_BY = 'CREATED_BY',
  MODIFIED_BY = 'MODIFIED_BY',
  CASCADE_COMPANY_ID = 'CASCADE_COMPANY_ID',
  REC_DELIVERY_METHODOLOGY = 'REC_DELIVERY_METHODOLOGY',
  ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS = 'ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS',
  PROPOSAL_SENDER = 'PROPOSAL_SENDER',
  UPSIDE_SHARING_FIXED_PRICE = 'UPSIDE_SHARING_FIXED_PRICE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  SETTLEMENT_INDEX = 'SETTLEMENT_INDEX',
  NEGOTIATION_STATUS = 'NEGOTIATION_STATUS',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  VPPA_LEGAL_ENTITY = 'VPPA_LEGAL_ENTITY',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  VPPA_STATUS = 'VPPA_STATUS',
  TENOR_START_DATE = 'TENOR_START_DATE',
  NEGATIVE_PRICING_METHOD = 'NEGATIVE_PRICING_METHOD',
  SELLER_ALLOCATED_UPSIDE_PERCENTAGE = 'SELLER_ALLOCATED_UPSIDE_PERCENTAGE',
  VPPA_REFERENCE = 'VPPA_REFERENCE',
  BUYER_UPSIDE_ALLOCATION = 'BUYER_UPSIDE_ALLOCATION',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  YEAR_ENTERED = 'YEAR_ENTERED',
  AUDIT_INFO = 'AUDIT_INFO',
  FORM_AGREEMENT_TYPE = 'FORM_AGREEMENT_TYPE',
  MONTH_ENTERED = 'MONTH_ENTERED',
  UPSIDE_SHARING = 'UPSIDE_SHARING',
  COLLAR = 'COLLAR',
  BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE = 'BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE',
  BUYER_UPSIDE_ALLOCATION_PRICE = 'BUYER_UPSIDE_ALLOCATION_PRICE',
  COLLAR_CAP_PRICE = 'COLLAR_CAP_PRICE',
  PROJECT_ID = 'PROJECT_ID',
  BUYERS_FRACTION = 'BUYERS_FRACTION',
  VPPA_AUDIT_ID = 'VPPA_AUDIT_ID',
  COLLAR_FLOOR_PRICE = 'COLLAR_FLOOR_PRICE',
  TICKER = 'TICKER',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  APPLICABLE_TRACKING_SYSTEM = 'APPLICABLE_TRACKING_SYSTEM',
  VPPA_SEQUENCE = 'VPPA_SEQUENCE',
  PERFORMANCE_GUARANTEE_METHOD = 'PERFORMANCE_GUARANTEE_METHOD',
  NEGOTIATION_ID = 'NEGOTIATION_ID',
  HUB_ZONE_MATRIX_ID = 'HUB_ZONE_MATRIX_ID',
  FIXED_PRICE = 'FIXED_PRICE',
  AUDIT_EVENT_TEXT = 'AUDIT_EVENT_TEXT',
}

export enum COLUMN_NEG_FIELDS {
  AUDIT_EVENT_TYPE = 'AUDIT_EVENT_TYPE',
  AUDIT_EVENT_DATETIME = 'AUDIT_EVENT_DATETIME',
  AUDIT_EVENT_USER = 'AUDIT_EVENT_USER',
  RECORD_CREATED = 'CREATED_AT',
  RECORD_MODIFIED = 'MODIFIED_AT',
  UNIQUE_ID = 'UNIQUE_ID',
  CREATED_BY = 'CREATED_BY',
  MODIFIED_BY = 'MODIFIED_BY',
  IS_EXCLUSIVE = 'IS_EXCLUSIVE',
  SELLER_VISIBILITY = 'SELLER_VISIBILITY',
  PROPOSAL_SENDER = 'PROPOSAL_SENDER',
  TENOR_MONTHS = 'TENOR_MONTHS',
  RFQ_ID = 'RFQ_ID',
  VPPA_ID = 'VPPA_ID',
  SELLER_COMPANY_NAME = 'SELLER_COMPANY_NAME',
  NEGOTIATION_STATUS = 'NEGOTIATION_STATUS',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  REQUEST_UUID = 'REQUEST_UUID',
  DOCUMENT_UUID = 'DOCUMENT_UUID',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  PROJECT_ID = 'PROJECT_ID',
  PROJECT_UUID = 'PROJECT_UUID',
  BUYER_COMPANY_ID = 'BUYER_COMPANY_ID',
  TENOR_START_DATE = 'TENOR_START_DATE',
  SELLER_COMPANY_ID = 'SELLER_COMPANY_ID',
  NEGOTIATION_AUDIT_ID = 'NEGOTIATION_AUDIT_ID',
  BUYER_VISIBILITY = 'BUYER_VISIBILITY',
  NEGOTIATION_ID = 'NEGOTIATION_ID',
  BUYER_COMPANY_NAME = 'BUYER_COMPANY_NAME',
  FIXED_PRICE = 'FIXED_PRICE',
  BUYER_POST_COD_COLLATERAL = 'BUYER_POST_COD_COLLATERAL',
  AUDIT_EVENT_TEXT = 'AUDIT_EVENT_TEXT',
  UNIQUE_TRANSACTION_IDENTIFIER = 'UNIQUE_TRANSACTION_IDENTIFIER',
  UPI = 'UPI',
}

export function AUDIT_RFQ_COLUMNS(context: AuditGrid) {
  return [
    {
      field: COLUMN_RFQ_FIELDS.AUDIT_EVENT_TYPE,
    },
    {
      field: COLUMN_RFQ_FIELDS.AUDIT_EVENT_DATETIME,
      valueFormatter: (params) => {
        if (params.data.AUDIT_EVENT_DATETIME) {
          return formatDateTime(params.data.AUDIT_EVENT_DATETIME);
        }
      },
    },
    {
      field: COLUMN_RFQ_FIELDS.AUDIT_EVENT_USER,
    },
    {
      field: COLUMN_RFQ_FIELDS.RECORD_CREATED,
      valueFormatter: (params) => {
        if (params.data.CREATED_AT) {
          return formatDateTime(params.data.CREATED_AT);
        }
      },
    },
    {
      field: COLUMN_RFQ_FIELDS.RECORD_MODIFIED,
      valueFormatter: (params) => {
        if (params.data.MODIFIED_AT) {
          return formatDateTime(params.data.MODIFIED_AT);
        }
      },
    },
    {
      field: COLUMN_RFQ_FIELDS.UNIQUE_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.CREATED_BY,
    },
    {
      field: COLUMN_RFQ_FIELDS.MODIFIED_BY,
    },
    {
      field: COLUMN_RFQ_FIELDS.CASCADE_COMPANY_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.SETTLEMENT_POINT,
    },
    {
      field: COLUMN_RFQ_FIELDS.NODAL_LME_HISTORIC_MIN,
    },
    {
      field: COLUMN_RFQ_FIELDS.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_SEQUENCE,
    },
    {
      field: COLUMN_RFQ_FIELDS.GUARANTEED_COD,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_APPLICABLE_REC_PROGRAM,
    },
    {
      field: COLUMN_RFQ_FIELDS.PROPOSAL_SENDER,
    },
    {
      field: COLUMN_RFQ_FIELDS.SETTLEMENT_INDEX_STRING,
    },
    {
      field: COLUMN_RFQ_FIELDS.UPSIDE_SHARING_FIXED_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.TENOR_MONTHS,
    },
    {
      field: COLUMN_RFQ_FIELDS.LME_RANGE_LOWER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_REFERENCE,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_STATUS,
    },
    {
      field: COLUMN_RFQ_FIELDS.STATE_NAME,
    },
    {
      field: COLUMN_RFQ_FIELDS.COUNTRY_NAME,
    },
    {
      field: COLUMN_RFQ_FIELDS.NEGOTIATION_STATUS,
    },
    {
      field: COLUMN_RFQ_FIELDS.FIXED_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.CAPACITY,
    },
    {
      field: COLUMN_RFQ_FIELDS.TENOR_START_DATE,
      valueFormatter: (params) => {
        if (params.data.TENOR_START_DATE) {
          return formatNumberToDate(params.data.TENOR_START_DATE);
        }
      },
    },
    {
      field: COLUMN_RFQ_FIELDS.FIXED_PRICE_LOWER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.SELLER_ALLOCATED_UPSIDE_PERCENTAGE,
    },
    {
      field: COLUMN_RFQ_FIELDS.NODAL_LME_HISTORIC_MAX,
    },
    {
      field: COLUMN_RFQ_FIELDS.FIXED_PRICE_UPPER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.BUYER_UPSIDE_ALLOCATION,
    },
    {
      field: COLUMN_RFQ_FIELDS.EXPECTED_SETTLEMENT_MIN,
    },
    {
      field: COLUMN_RFQ_FIELDS.ESTIMATED_DOWNSIDE_RISK_UPPER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.CONTRACT_TYPE,
    },
    {
      field: COLUMN_RFQ_FIELDS.REQUESTED_VOLUME,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_LEGAL_ENTITY,
    },
    {
      field: COLUMN_RFQ_FIELDS.CITY_NAME,
    },
    {
      field: COLUMN_RFQ_FIELDS.YEAR_ENTERED,
    },
    {
      field: COLUMN_RFQ_FIELDS.EXPECTED_SETTLEMENT_MAX,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_PERFORMANCE_GUARANTEE_METHOD,
    },
    {
      field: COLUMN_RFQ_FIELDS.MONTH_ENTERED,
    },
    {
      field: COLUMN_RFQ_FIELDS.UPSIDE_SHARING,
    },
    {
      field: COLUMN_RFQ_FIELDS.DOWNSIDE_RISK_MAX,
    },
    {
      field: COLUMN_RFQ_FIELDS.COLLAR,
    },
    {
      field: COLUMN_RFQ_FIELDS.BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_NEGATIVE_PRICING_METHOD,
    },
    {
      field: COLUMN_RFQ_FIELDS.BUYER_UPSIDE_ALLOCATION_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.TARGET_COD,
      valueFormatter: (params) => {
        if (params.data.TARGET_COD) {
          return formatNumberToDate(params.data.TARGET_COD);
        }
      },
    },
    {
      field: COLUMN_RFQ_FIELDS.COLLAR_CAP_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.DOWNSIDE_RISK_MIN,
    },
    {
      field: COLUMN_RFQ_FIELDS.TECHNOLOGY,
    },
    {
      field: COLUMN_RFQ_FIELDS.CARBON_FREE_ENERGY_LOWER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.EXPECTED_SETTLEMENT_VALUE_LOWER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.CARBON_FREE_ENERGY_UPPER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.ISO,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_AUDIT_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.STATE_ABBREVIATION,
    },
    {
      field: COLUMN_RFQ_FIELDS.IMPLIED_CARBON_PRICE_MAX,
    },
    {
      field: COLUMN_RFQ_FIELDS.COLLAR_FLOOR_PRICE,
    },
    {
      field: COLUMN_RFQ_FIELDS.LME_RANGE_UPPER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.TICKER,
    },
    {
      field: COLUMN_RFQ_FIELDS.ESTIMATED_DOWNSIDE_RISK_LOWER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.EXPECTED_SETTLEMENT_VALUE_UPPER_BOUND,
    },
    {
      field: COLUMN_RFQ_FIELDS.NEGOTIATION_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.RFQ_REC_DELIVERY_METHODOLOGY,
    },
    {
      field: COLUMN_RFQ_FIELDS.IMPLIED_CARBON_PRICE_MIN,
    },
    {
      field: COLUMN_RFQ_FIELDS.HUB_ZONE_MATRIX_ID,
    },
    {
      field: COLUMN_RFQ_FIELDS.BUYER_POST_COD_COLLATERAL,
    },
    {
      field: COLUMN_RFQ_FIELDS.AUDIT_EVENT_TEXT,
    },
  ];
}

export function AUDIT_VPPA_COLUMNS(context: AuditGrid) {
  return [
    {
      field: COLUMN_VPPA_FIELDS.AUDIT_EVENT_TYPE,
    },
    {
      field: COLUMN_VPPA_FIELDS.AUDIT_EVENT_DATETIME,
      valueFormatter: (params) => {
        if (params.data.AUDIT_EVENT_DATETIME) {
          return formatDateTime(params.data.AUDIT_EVENT_DATETIME);
        }
      },
    },
    {
      field: COLUMN_VPPA_FIELDS.AUDIT_EVENT_USER,
    },
    {
      field: COLUMN_VPPA_FIELDS.RECORD_CREATED,
      valueFormatter: (params) => {
        if (params.data.CREATED_AT) {
          return formatDateTime(params.data.CREATED_AT);
        }
      },
    },
    {
      field: COLUMN_VPPA_FIELDS.RECORD_MODIFIED,
      valueFormatter: (params) => {
        if (params.data.MODIFIED_AT) {
          return formatDateTime(params.data.MODIFIED_AT);
        }
      },
    },
    {
      field: COLUMN_VPPA_FIELDS.UNIQUE_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.CREATED_BY,
    },
    {
      field: COLUMN_VPPA_FIELDS.MODIFIED_BY,
    },
    {
      field: COLUMN_VPPA_FIELDS.CASCADE_COMPANY_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.REC_DELIVERY_METHODOLOGY,
    },
    {
      field: COLUMN_VPPA_FIELDS.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS,
    },
    {
      field: COLUMN_VPPA_FIELDS.PROPOSAL_SENDER,
    },
    {
      field: COLUMN_VPPA_FIELDS.UPSIDE_SHARING_FIXED_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.TENOR_MONTHS,
    },
    {
      field: COLUMN_VPPA_FIELDS.SETTLEMENT_INDEX,
    },
    {
      field: COLUMN_VPPA_FIELDS.NEGOTIATION_STATUS,
    },
    {
      field: COLUMN_VPPA_FIELDS.OFFERED_CAPACITY,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_LEGAL_ENTITY,
    },
    {
      field: COLUMN_VPPA_FIELDS.SELLER_POST_COD_COLLATERAL,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_STATUS,
    },
    {
      field: COLUMN_VPPA_FIELDS.TENOR_START_DATE,
      valueFormatter: (params) => {
        if (params.data.TENOR_START_DATE) {
          return formatNumberToDate(params.data.TENOR_START_DATE);
        }
      },
    },
    {
      field: COLUMN_VPPA_FIELDS.NEGATIVE_PRICING_METHOD,
    },
    {
      field: COLUMN_VPPA_FIELDS.SELLER_ALLOCATED_UPSIDE_PERCENTAGE,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_REFERENCE,
    },
    {
      field: COLUMN_VPPA_FIELDS.BUYER_UPSIDE_ALLOCATION,
    },
    {
      field: COLUMN_VPPA_FIELDS.CONTRACT_TYPE,
    },
    {
      field: COLUMN_VPPA_FIELDS.SETTLEMENT_POINT,
    },
    {
      field: COLUMN_VPPA_FIELDS.YEAR_ENTERED,
    },
    {
      field: COLUMN_VPPA_FIELDS.AUDIT_INFO,
    },
    {
      field: COLUMN_VPPA_FIELDS.FORM_AGREEMENT_TYPE,
    },
    {
      field: COLUMN_VPPA_FIELDS.MONTH_ENTERED,
    },
    {
      field: COLUMN_VPPA_FIELDS.UPSIDE_SHARING,
    },
    {
      field: COLUMN_VPPA_FIELDS.COLLAR,
    },
    {
      field: COLUMN_VPPA_FIELDS.BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.BUYER_UPSIDE_ALLOCATION_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.COLLAR_CAP_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.PROJECT_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.BUYERS_FRACTION,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_AUDIT_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.COLLAR_FLOOR_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.TICKER,
    },
    {
      field: COLUMN_VPPA_FIELDS.APPLICABLE_REC_PROGRAM,
    },
    {
      field: COLUMN_VPPA_FIELDS.APPLICABLE_TRACKING_SYSTEM,
    },
    {
      field: COLUMN_VPPA_FIELDS.VPPA_SEQUENCE,
    },
    {
      field: COLUMN_VPPA_FIELDS.PERFORMANCE_GUARANTEE_METHOD,
    },
    {
      field: COLUMN_VPPA_FIELDS.NEGOTIATION_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.HUB_ZONE_MATRIX_ID,
    },
    {
      field: COLUMN_VPPA_FIELDS.FIXED_PRICE,
    },
    {
      field: COLUMN_VPPA_FIELDS.AUDIT_EVENT_TEXT,
    },
  ];
}

export function AUDIT_NEGOTIATION_COLUMNS(context: AuditGrid) {
  return [
    {
      field: COLUMN_NEG_FIELDS.AUDIT_EVENT_TYPE,
    },
    {
      field: COLUMN_NEG_FIELDS.AUDIT_EVENT_DATETIME,
      valueFormatter: (params) => {
        if (params.data.AUDIT_EVENT_DATETIME) {
          return formatDateTime(params.data.AUDIT_EVENT_DATETIME);
        }
      },
    },
    {
      field: COLUMN_NEG_FIELDS.AUDIT_EVENT_USER,
    },
    {
      field: COLUMN_NEG_FIELDS.RECORD_CREATED,
      valueFormatter: (params) => {
        if (params.data.CREATED_AT) {
          return formatDateTime(params.data.CREATED_AT);
        }
      },
    },
    {
      field: COLUMN_NEG_FIELDS.RECORD_MODIFIED,
      valueFormatter: (params) => {
        if (params.data.MODIFIED_AT) {
          return formatDateTime(params.data.MODIFIED_AT);
        }
      },
    },
    {
      field: COLUMN_NEG_FIELDS.UNIQUE_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.CREATED_BY,
    },
    {
      field: COLUMN_NEG_FIELDS.MODIFIED_BY,
    },
    {
      field: COLUMN_NEG_FIELDS.IS_EXCLUSIVE,
    },
    {
      field: COLUMN_NEG_FIELDS.SELLER_VISIBILITY,
    },
    {
      field: COLUMN_NEG_FIELDS.PROPOSAL_SENDER,
    },
    {
      field: COLUMN_NEG_FIELDS.TENOR_MONTHS,
    },
    {
      field: COLUMN_NEG_FIELDS.RFQ_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.VPPA_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.SELLER_COMPANY_NAME,
    },
    {
      field: COLUMN_NEG_FIELDS.NEGOTIATION_STATUS,
    },
    {
      field: COLUMN_NEG_FIELDS.OFFERED_CAPACITY,
    },
    {
      field: COLUMN_NEG_FIELDS.REQUEST_UUID,
    },
    {
      field: COLUMN_NEG_FIELDS.DOCUMENT_UUID,
    },
    {
      field: COLUMN_NEG_FIELDS.SELLER_POST_COD_COLLATERAL,
    },
    {
      field: COLUMN_NEG_FIELDS.PROJECT_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.PROJECT_UUID,
    },
    {
      field: COLUMN_NEG_FIELDS.BUYER_COMPANY_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.TENOR_START_DATE,
      valueFormatter: (params) => {
        if (params.data.TENOR_START_DATE) {
          return formatNumberToDate(params.data.TENOR_START_DATE);
        }
      },
    },
    {
      field: COLUMN_NEG_FIELDS.SELLER_COMPANY_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.NEGOTIATION_AUDIT_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.BUYER_VISIBILITY,
    },
    {
      field: COLUMN_NEG_FIELDS.NEGOTIATION_ID,
    },
    {
      field: COLUMN_NEG_FIELDS.BUYER_COMPANY_NAME,
    },
    {
      field: COLUMN_NEG_FIELDS.FIXED_PRICE,
    },
    {
      field: COLUMN_NEG_FIELDS.BUYER_POST_COD_COLLATERAL,
    },
    {
      field: COLUMN_NEG_FIELDS.AUDIT_EVENT_TEXT,
    },
    {
      field: COLUMN_NEG_FIELDS.UNIQUE_TRANSACTION_IDENTIFIER,
    },
    {
      field: COLUMN_NEG_FIELDS.UPI,
    },
  ];
}
